import { useState } from 'react';
import { X } from 'lucide-react';
import { AdminOverlayPortal } from './AdminOverlayPortal';

type Props = {
  title: string;
  description: string;
  confirmLabel: string;
  expectedPhrase: string;
  placeholder?: string;
  danger?: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
};

export function AdminConfirmSheet({
  title,
  description,
  confirmLabel,
  expectedPhrase,
  placeholder,
  danger,
  onCancel,
  onConfirm,
}: Props) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const match = typed.trim() === expectedPhrase;

  const submit = async () => {
    if (!match || busy) return;
    setBusy(true);
    try {
      await onConfirm();
    } finally {
      setBusy(false);
    }
  };

  return (
    <AdminOverlayPortal zIndex={95}>
      <div className="flex-1 flex items-end justify-center min-h-0">
        <div
          className="w-full max-w-lg rounded-t-2xl p-4 space-y-3"
          style={{ background: '#0F2340', borderTop: '1px solid var(--border-subtle)' }}
        >
          <div className="flex items-start justify-between gap-2">
            <div>
              <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>
                {title}
              </p>
              <p className="text-[11px] mt-1 leading-relaxed" style={{ color: 'var(--text-3)' }}>
                {description}
              </p>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="press p-2 rounded-full shrink-0 min-h-[44px] min-w-[44px] touch-manipulation"
              style={{ background: 'rgba(255,255,255,0.08)' }}
            >
              <X size={18} color="var(--text-2)" />
            </button>
          </div>
          <p className="text-[10px] font-mono px-2 py-1 rounded-lg" style={{ background: 'rgba(255,255,255,0.05)', color: '#F6AD55' }}>
            {expectedPhrase}
          </p>
          <input
            className="input-glass w-full text-sm min-h-[48px] touch-manipulation font-mono"
            placeholder={placeholder ?? expectedPhrase}
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
          />
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onCancel}
              className="press flex-1 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
              style={{ color: 'var(--text-3)' }}
            >
              Отмена
            </button>
            <button
              type="button"
              disabled={!match || busy}
              onClick={() => void submit()}
              className="press flex-1 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px] disabled:opacity-40"
              style={
                danger
                  ? { background: 'rgba(252,129,74,0.2)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.4)' }
                  : { background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }
              }
            >
              {busy ? '…' : confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </AdminOverlayPortal>
  );
}
